<template>
  <div class="pr-4 pb-16 max-w-2xl">
    <h1 class="text-2xl uppercase font-semibold text-white">
      {{ $t("account.profile") }}
    </h1>
    <AccountTabs />

    <!-- Account details -->
    <section class="mt-10">
      <h2 class="text-lg font-semibold text-white">
        {{ $t("account.details") }}
      </h2>
      <p class="text-subtle text-sm mt-1">
        {{ $t("account.detailsHint") }}
      </p>

      <form class="mt-6 flex flex-col gap-5" @submit.prevent="saveProfile">
        <InputField
          v-model="form.name"
          :label="$t('account.name')"
          name="name"
          autocomplete="name"
          required
        />
        <InputField
          v-model="form.email"
          :label="$t('account.email')"
          type="email"
          name="email"
          inputmode="email"
          autocomplete="email"
          required
        />

        <!-- Verification status: the API exposes it and it explains why
             notification emails may not be arriving. -->
        <p v-if="user && !user.verified" class="text-subtle text-sm">
          <Font-awesome-icon
            :icon="['fas', 'circle-exclamation']"
            class="text-highlight mr-1"
            aria-hidden="true"
          />
          {{ $t("account.unverified") }}
        </p>

        <div class="flex items-center gap-4">
          <Button :loading="savingProfile" class="uppercase tracking-widest">
            {{ $t("account.save") }}
          </Button>
          <p v-if="profileSaved" role="status" class="text-highlight text-sm">
            {{ $t("account.saved") }}
          </p>
        </div>
        <p v-if="emailChanged" role="status" class="text-highlight text-sm">
          {{ $t("account.emailChanged") }}
        </p>
        <ErrorLabel :error="profileError" />
      </form>
    </section>

    <!-- Password -->
    <section class="mt-14 border-t border-primary-400 pt-10">
      <h2 class="text-lg font-semibold text-white">
        {{ $t("account.password") }}
      </h2>
      <p class="text-subtle text-sm mt-1">
        {{ $t("account.passwordHint") }}
      </p>

      <form class="mt-6 flex flex-col gap-5" @submit.prevent="savePassword">
        <InputField
          v-model="password.current"
          :label="$t('account.currentPassword')"
          type="password"
          name="current_password"
          autocomplete="current-password"
          required
        />
        <InputField
          v-model="password.next"
          :label="$t('account.newPassword')"
          type="password"
          name="password"
          autocomplete="new-password"
          required
        />
        <InputField
          v-model="password.confirm"
          :label="$t('account.confirmPassword')"
          type="password"
          name="password_confirmation"
          autocomplete="new-password"
          required
        />

        <div class="flex items-center gap-4">
          <Button :loading="savingPassword" class="uppercase tracking-widest">
            {{ $t("account.updatePassword") }}
          </Button>
          <p v-if="passwordSaved" role="status" class="text-highlight text-sm">
            {{ $t("account.passwordUpdated") }}
          </p>
        </div>
        <ErrorLabel :error="passwordError" />
      </form>
    </section>
  </div>
</template>

<script>
import AccountTabs from "@/components/account/AccountTabs.vue";
import Button from "@/components/common/Button.vue";
import ErrorLabel from "@/components/common/ErrorLabel.vue";

const MIN_PASSWORD_LENGTH = 6;

export default {
  name: "Profile",
  components: { AccountTabs, Button, ErrorLabel },

  data() {
    return {
      form: { name: "", email: "" },
      password: { current: "", next: "", confirm: "" },
      savingProfile: false,
      savingPassword: false,
      profileSaved: false,
      emailChanged: false,
      passwordSaved: false,
      profileError: "",
      passwordError: "",
    };
  },

  computed: {
    user() {
      return this.$store.getters.authUser;
    },
  },

  watch: {
    // The user object arrives asynchronously on a hard page load.
    user: {
      immediate: true,
      handler(user) {
        if (user) this.resetForm(user);
      },
    },
  },

  methods: {
    resetForm(user) {
      this.form.name = user.name ?? "";
      this.form.email = user.email ?? "";
    },

    /** Surface the API's own field messages rather than a generic failure. */
    validationMessage(error, fallbackKey = "auth.error.update") {
      const firstField = Object.values(error?.response?.data?.errors ?? {})[0];
      if (Array.isArray(firstField) && firstField.length) return firstField[0];
      return this.$t(fallbackKey).toString();
    },

    saveProfile() {
      if (this.savingProfile) return;
      this.savingProfile = true;
      this.profileError = "";
      this.profileSaved = false;
      this.emailChanged = false;

      this.$store
        .dispatch("updateProfile", {
          name: this.form.name.trim(),
          email: this.form.email.trim(),
        })
        .then((response) => {
          // A new address resets verification, so say so instead of a bare
          // "Saved" that hides the fact they now can't log in until they
          // click the link.
          this.profileSaved = !response.emailChanged;
          this.emailChanged = Boolean(response.emailChanged);
        })
        .catch((error) => {
          this.profileError = this.validationMessage(error);
        })
        .finally(() => (this.savingProfile = false));
    },

    /** Client-side checks first, so obvious mistakes never reach the server. */
    validatePassword() {
      if (!this.password.current) {
        return this.$t("auth.error.passwordMissing").toString();
      }
      if (this.password.next.length < MIN_PASSWORD_LENGTH) {
        return this.$t("auth.error.passwordValidation").toString();
      }
      if (this.password.next !== this.password.confirm) {
        return this.$t("auth.error.passwordMatch").toString();
      }
      return "";
    },

    savePassword() {
      if (this.savingPassword) return;

      const validationError = this.validatePassword();
      if (validationError) {
        this.passwordError = validationError;
        this.passwordSaved = false;
        return;
      }

      this.savingPassword = true;
      this.passwordError = "";
      this.passwordSaved = false;

      this.$store
        .dispatch("changePassword", {
          current_password: this.password.current,
          password: this.password.next,
          password_confirmation: this.password.confirm,
        })
        .then(() => {
          this.passwordSaved = true;
          // Never leave passwords sitting in the form after a success.
          this.password.current = "";
          this.password.next = "";
          this.password.confirm = "";
        })
        .catch((error) => {
          // The API answers 422 + code "invalid_password" when the current
          // password is wrong, and a plain 422 for validation failures.
          const data = error?.response?.data;
          if (data?.code === "invalid_password") {
            this.passwordError = this.$t("auth.error.passwordInvalid");
          } else if (error?.response?.status === 422) {
            this.passwordError = this.validationMessage(
              error,
              "auth.error.passwordUpdate",
            );
          } else {
            this.passwordError = this.$t("auth.error.passwordUpdate");
          }
          this.passwordError = this.passwordError.toString();
        })
        .finally(() => (this.savingPassword = false));
    },
  },
};
</script>
